import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/shared";
import { MILESTONE_TRIGGERS, fmtMoney } from "@/lib/constants";

// Milestones are always a plain amount now; legacy % milestones open pre-filled with their computed amount.
const init = (m) => ({ name: m?.name || "", value: m ? (m.amount_type === "fixed" ? m.value : m.amount ?? "") : "", trigger: m?.trigger || "manual", component: m?.component || null,
  checklist_item_ids: m?.checklist_item_ids || [], require_all: m?.require_all ?? true, notes: m?.notes || "" });

export const MilestoneDialog = ({ open, onOpenChange, milestone, project: p, onSave }) => {
  const [f, setF] = useState(init(milestone));
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(init(milestone)); }, [open, milestone]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const fee = p.fee_amount || 0;
  const amount = f.value === "" ? 0 : Number(f.value);
  const othersTotal = (p.milestones || []).filter((m) => m.id !== milestone?.id).reduce((a, m) => a + (m.amount || 0), 0);
  const over = fee > 0 && othersTotal + amount > fee + 0.01;
  const toggleItem = (id) => setF((x) => ({ ...x, checklist_item_ids: x.checklist_item_ids.includes(id) ? x.checklist_item_ids.filter((i) => i !== id) : [...x.checklist_item_ids, id] }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({ ...(milestone || {}), ...f, amount_type: "fixed", value: Number(f.value), expected_invoice_date: null, expected_receipt_date: null,
        checklist_item_ids: f.trigger === "checklist" ? f.checklist_item_ids : [] });
      onOpenChange(false);
    } catch { /* toast by caller */ } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="milestone-dialog">
        <DialogHeader>
          <DialogTitle>{milestone ? "Edit payment milestone" : "Add payment milestone"}</DialogTitle>
          <DialogDescription>Agreed fee: <span className="font-medium text-slate-800">{fmtMoney(fee, p.fee_currency)}</span>{fee ? ` · ${fmtMoney(Math.max(fee - othersTotal, 0), p.fee_currency)} not yet allocated` : ""}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid grid-cols-[1fr_170px_170px] gap-3">
            <Field label="Milestone name" required><Input data-testid="ms-name-input" value={f.name} onChange={set("name")} required placeholder="e.g. Advance on acceptance" /></Field>
            <Field label={`Amount (${p.fee_currency})`} required><Input data-testid="ms-value-input" type="number" min="0.01" step="any" value={f.value} onChange={set("value")} required className={over ? "border-rose-400" : ""} /></Field>
            <Field label="Covers">
              <Select value={f.component || "none"} onValueChange={(v) => set("component")(v === "none" ? null : v)}>
                <SelectTrigger data-testid="ms-component-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">Not specified</SelectItem><SelectItem value="project_fee">Project fee</SelectItem><SelectItem value="passthrough">Pass-through costs</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          {over && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2" data-testid="ms-over-warning">This would allocate {fmtMoney(othersTotal + amount, p.fee_currency)} in total, more than the agreed fee. Reduce the amount to save.</div>}
          <Field label="Trigger — when does this become ready to invoice?">
            <Select value={f.trigger} onValueChange={set("trigger")}>
              <SelectTrigger data-testid="ms-trigger-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MILESTONE_TRIGGERS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {f.trigger === "checklist" && (
            <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="ms-checklist-links">
              <div className="text-xs font-medium text-slate-600">Linked checklist tasks</div>
              {(p.checklist || []).length === 0 && <div className="text-xs text-slate-400">No checklist tasks yet — add them in Checklist & Team first.</div>}
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {(p.checklist || []).map((it) => (
                  <label key={it.id} className="flex items-center gap-2 text-sm text-slate-700">
                    <Checkbox checked={f.checklist_item_ids.includes(it.id)} onCheckedChange={() => toggleItem(it.id)} data-testid={`ms-link-${it.id}`} />
                    <span className={it.status === "completed" ? "text-slate-400 line-through" : ""}>{it.title}</span>
                  </label>
                ))}
              </div>
              {f.checklist_item_ids.length > 1 && (
                <Select value={f.require_all ? "all" : "any"} onValueChange={(v) => set("require_all")(v === "all")}>
                  <SelectTrigger data-testid="ms-require-select" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All linked tasks must be completed</SelectItem><SelectItem value="any">Any one linked task completed is enough</SelectItem></SelectContent>
                </Select>
              )}
            </div>
          )}
          <Field label="Notes"><Input data-testid="ms-notes-input" value={f.notes} onChange={set("notes")} /></Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="ms-submit-button" type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy || over || f.value === "" || (f.trigger === "checklist" && f.checklist_item_ids.length === 0)}>{busy ? "Saving…" : "Save milestone"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
